import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link, useParams, useRouteContext } from '@tanstack/react-router';
import { useState } from 'react';
import { ErrorState, MetricTooltip, Select } from '@bb/ui';
import { ApiError, type HomeKpi, type RecentObjectView } from '../../api/client.ts';
import { formatCount } from '../audience/ChannelReachBreakdown.tsx';
import { formatWhen } from '../segments/SegmentsListPage.tsx';
import { HomeChart } from './HomeChart.tsx';

const PERIODS = [7, 30, 90] as const;
const TYPE_LABELS: Record<RecentObjectView['objectType'], string> = { campaign: 'Campaign', canvas: 'Canvas', segment: 'Segment' };

/** `1 204`, `41.2 %`, `2.35`; a missing ratio is a dash. */
export function formatKpi(kpi: Pick<HomeKpi, 'format' | 'value'>): string {
  if (kpi.value === null) return '—';
  if (kpi.format === 'percent') return `${(kpi.value * 100).toFixed(1)} %`;
  if (kpi.format === 'ratio') return kpi.value.toFixed(2);
  return formatCount(Math.round(kpi.value * 10) / 10);
}

function KpiGrid({ kpis }: { kpis: HomeKpi[] }) {
  return (
    <ul aria-label="Key metrics" className="kpi-grid">
      {kpis.map((kpi) => (
        <li key={kpi.key} className="kpi-grid__tile">
          <span className="kpi-grid__title">{kpi.title.en}</span>
          <MetricTooltip title={kpi.title.en} value={formatKpi(kpi)} formula={kpi.formula.en} expression={kpi.expression} {...(kpi.value === null ? { explanation: 'No monthly active users in the period.' } : {})} />
        </li>
      ))}
    </ul>
  );
}

function RecentObjects({ environmentId }: { environmentId: string }) {
  const { api } = useRouteContext({ from: '/env/$environmentId/home' });
  const recent = useQuery({ queryKey: ['home', environmentId, 'recent'], queryFn: () => api.recentObjects(environmentId) });
  const link = (o: RecentObjectView) => {
    const label = `${TYPE_LABELS[o.objectType]} ${o.name}`;
    if (o.objectType === 'campaign')
      return (
        <Link to="/env/$environmentId/campaigns/$campaignId" params={{ environmentId, campaignId: o.objectId }}>
          {label}
        </Link>
      );
    if (o.objectType === 'canvas')
      return (
        <Link to="/env/$environmentId/canvas/$canvasId" params={{ environmentId, canvasId: o.objectId }}>
          {label}
        </Link>
      );
    return (
      <Link to="/env/$environmentId/audience/segments/$segmentId" params={{ environmentId, segmentId: o.objectId }}>
        {label}
      </Link>
    );
  };
  return (
    <section aria-label="Pick up where you left off" className="panel">
      <h2>Pick up where you left off</h2>
      {recent.data?.items.length ? (
        <ul aria-label="Recent objects">
          {recent.data.items.map((o) => (
            <li key={`${o.objectType}/${o.objectId}`}>
              {link(o)}{' '}
              <span className="hint">
                {o.reason === 'edited' ? 'changed' : 'viewed'} {formatWhen(o.at)}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        recent.data && <p className="hint">Campaigns, Canvases and segments you open or change show up here.</p>
      )}
    </section>
  );
}

/**
 * Environment Home (BB-133): usage KPIs with their formulas, daily DAU / MAU, sessions and new users, the split by
 * app, and the employee's recent objects. Numbers come from the session aggregates refreshed every 5 minutes.
 */
export function HomePage() {
  const { environmentId } = useParams({ from: '/env/$environmentId/home' });
  const { api } = useRouteContext({ from: '/env/$environmentId/home' });
  const [days, setDays] = useState<(typeof PERIODS)[number]>(30);
  const [app, setApp] = useState('');
  // The previous answer stays on screen while another period or app loads; `byApp` always lists every app.
  const view = useQuery({ queryKey: ['home', environmentId, days, app], queryFn: () => api.home(environmentId, { days, ...(app ? { app } : {}) }), placeholderData: keepPreviousData });
  const appOptions = view.data?.byApp ?? [];
  return (
    <section>
      <h1>Home</h1>
      <div className="toolbar">
        <label>
          App{' '}
          <Select aria-label="App" value={app} onChange={(e) => setApp(e.target.value)} options={[{ value: '', label: 'All apps' }, ...appOptions.map((a) => ({ value: a.appInstanceId, label: a.name }))]} />
        </label>
        <label>
          Period{' '}
          <Select aria-label="Period" value={String(days)} onChange={(e) => setDays(Number(e.target.value) as (typeof PERIODS)[number])} options={PERIODS.map((d) => ({ value: String(d), label: `Last ${d} days` }))} />
        </label>
      </div>
      {view.isPending ? (
        <p aria-busy="true">Loading Home…</p>
      ) : view.isError ? (
        <ErrorState title="Home could not be loaded" message={view.error instanceof ApiError ? view.error.message : 'Something went wrong.'} onRetry={() => void view.refetch()} />
      ) : (
        <>
          <p className="hint">
            {view.data.from} – {view.data.to}, days in {view.data.timezone}. {view.data.freshness ? `Sessions counted up to ${formatWhen(view.data.freshness)}.` : 'Not aggregated yet: numbers appear within 5 minutes of the first sessions.'}
          </p>
          <KpiGrid kpis={view.data.kpis} />
          <div className="home-charts">
            <HomeChart
              title="Active users"
              days={view.data.series}
              series={[
                { label: 'DAU', value: (d) => d.dau },
                { label: 'MAU', value: (d) => d.mau, dashed: true },
              ]}
            />
            <HomeChart title="Daily sessions" days={view.data.series} series={[{ label: 'Sessions', value: (d) => d.sessions }]} />
            <HomeChart title="New users" days={view.data.series} series={[{ label: 'New users', value: (d) => d.newUsers }]} />
          </div>
          {!app && (
            <table className="table" aria-label="By app">
              <thead>
                <tr>
                  <th scope="col">App</th>
                  <th scope="col">Platform</th>
                  <th scope="col">Users</th>
                  <th scope="col">Sessions</th>
                  <th scope="col">Average DAU</th>
                  <th scope="col">MAU</th>
                </tr>
              </thead>
              <tbody>
                {view.data.byApp.map((a) => (
                  <tr key={a.appInstanceId}>
                    <th scope="row">{a.name}</th>
                    <td>{a.platform}</td>
                    <td>{formatCount(a.users)}</td>
                    <td>{formatCount(a.sessions)}</td>
                    <td>{formatCount(Math.round(a.dau * 10) / 10)}</td>
                    <td>{formatCount(a.mau)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
      <RecentObjects environmentId={environmentId} />
    </section>
  );
}
